/** Normalized underscore form, e.g. `en_US`, `es_MX`. */
export type Locale = string;

export const DEFAULT_LOCALE: Locale = 'en_US';

/** Locales with authored bundles. Add an entry here to support a new locale. */
export const SUPPORTED_LOCALES: Locale[] = ['en_US', 'es_MX'];

/** Geo used for the fallback chain before CloudFront meta resolves. Matches the app store default. */
export const DEFAULT_VIEWER_COUNTRY = 'US';

export const toUnderscore = (bcp47: string): Locale => bcp47.replace('-', '_');

export const toBcp47 = (locale: Locale): string => locale.replace('_', '-');

type Bundle = Record<string, string>;
export type Vars = Record<string, string | number>;

export type LocaleSources =
	| Record<Locale, Bundle>
	| undefined
	| Array<Record<Locale, Bundle> | undefined>;

/**
 * Builds the active, geographic, and source-locale fallback chain.
 * @param {Locale} locale - Active display locale.
 * @param {string} viewerCountry - Two-letter viewer country code.
 * @returns {Locale[]} Ordered, deduplicated locale chain.
 */
export function buildFallbackChain(locale: Locale, viewerCountry: string): Locale[] {
	const geoDefault: Locale = viewerCountry.toUpperCase() === 'US' ? 'en_US' : 'en_GB';
	const chain: Locale[] = [locale, geoDefault];
	if (geoDefault === 'en_GB') chain.push('en_US');
	return [...new Set(chain)];
}

const PLACEHOLDER = /\$\{([\w.:-]+)\}/g;

/**
 * Converts a single registry into the common source-list shape.
 * @param {LocaleSources} sources - One registry or an ordered registry list.
 * @returns {Array<Record<Locale, Bundle> | undefined>} Ordered registry list.
 */
function normalizeSources(sources: LocaleSources): Array<Record<Locale, Bundle> | undefined> {
	return Array.isArray(sources) ? sources : [sources];
}

/**
 * Finds the first matching translation in source and locale order.
 * @param {LocaleSources} sources - Ordered bundle registries.
 * @param {Locale[]} chain - Ordered locale fallback chain.
 * @param {string} key - Runtime bundle key.
 * @returns {string | undefined} Translation template when found.
 */
function lookup(sources: LocaleSources, chain: Locale[], key: string): string | undefined {
	for (const byLocale of normalizeSources(sources)) {
		for (const locale of chain) {
			const hit = byLocale?.[locale]?.[key];
			if (hit != null) return hit;
		}
	}
	return undefined;
}

/**
 * Resolves a key and interpolates explicit variables or nested bundle keys.
 * @param {LocaleSources} sources - Ordered bundle registries.
 * @param {Locale[]} chain - Ordered locale fallback chain.
 * @param {string} key - Runtime bundle key.
 * @param {Vars} [vars] - Optional interpolation values.
 * @param {Set<string>} [ancestry] - Internal cycle guard.
 * @returns {string} Resolved copy, or the raw key when missing.
 */
export function translate(
	sources: LocaleSources,
	chain: Locale[],
	key: string,
	vars?: Vars,
	ancestry: Set<string> = new Set()
): string {
	let template = lookup(sources, chain, key);
	if (template == null && key.includes('.') && !key.includes('::')) {
		template = lookup(sources, chain, key.replaceAll('.', '::'));
	}
	if (template == null) return key;

	const nextAncestry = new Set(ancestry).add(key);
	return template.replace(PLACEHOLDER, (literal, name: string) => {
		if (vars && name in vars) return String(vars[name]);
		if (!nextAncestry.has(name) && lookup(sources, chain, name) != null) {
			return translate(sources, chain, name, vars, nextAncestry);
		}
		return literal;
	});
}
